/**
 * 원격 상태 점검 접속 정보 — 현장 설정 페이지(기본 정보와 담당자 사이).
 *
 * 매일 아침 관리 백엔드(site-health, #1391)가 여기 등록한 주소로 각 현장에
 * 붙는다. 'IP / 네트워크' 의 네트워크 스프레드시트와 목적이 다르다:
 * 스프레드시트는 사람이 보는 IP 대장이라 현장마다 컬럼이 제각각인데(실제로 어떤
 * 현장은 'IP 주소' 컬럼이 두 개다), 잡은 기계가 읽을 수 있는 값이어야 한다.
 * 그래서 현장당 딱 두 줄만, 현장을 정의하는 현장 설정에서 따로 받는다.
 *
 * 저장 위치는 site_network_devices 이고 category 로 역할을 구분한다:
 *   integration-server  명령이 실제로 도는 통합서버 (필수)
 *   access-pc           경유하는 접속 PC (선택 — 없으면 통합서버로 직접 붙는다)
 *   video-server        영상처리서버 (여러 대, 통합서버 경유)
 *   nvr                 NVR (여러 대, 통합서버에서 핑)
 *
 * 방재실이 여러 곳인 현장(중외공원 2블럭·3블럭 방재실)은 방재실마다 위 장비가 따로라
 * health_group(방재실 이름)으로 묶는다. 방재실이 하나면 이름은 비워도 된다.
 *
 * 비밀번호도 여기서 받는다 — 접속 정보를 콘솔 한 곳에서 관리하기로 했다. 백엔드는
 * 자기 SSH 키를 먼저 시도하고, 안 되면 이 비밀번호로 붙는다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { HealthAndSafety as HealthIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

import { SiteNetworkDeviceService } from '../../services/SiteNetworkDeviceService';
import { SiteProjectService } from '../../services/SiteProjectService';
import { SiteNetworkDevice } from '../../models/SiteNetworkDevice';
import { getUserInfo } from '../../utils/auth';

/** 백엔드(entity.CategoryIntegrationServer / CategoryAccessPC)와 같은 값이어야 한다. */
export const CATEGORY_INTEGRATION_SERVER = 'integration-server';
export const CATEGORY_ACCESS_PC = 'access-pc';
/** 영상처리서버 — 현장마다 여러 대, 통합서버를 경유해 점검한다. */
export const CATEGORY_VIDEO_SERVER = 'video-server';
/** NVR(녹화기) — 방재실마다 여러 대, 통합서버에서 핑으로만 확인한다. */
export const CATEGORY_NVR = 'nvr';

/**
 * 사람이 덧붙인 지시가 시작되는 줄. 백엔드는 이 줄까지를 잘라내고 뒤만 Claude 에게
 * 넘긴다(site-health/usecase/summarize.go 의 promptMarker) — 앞부분은 이미
 * 점검하는 항목을 적어 둔 안내문이라 두 번 보내면 요약이 같은 말을 되풀이한다.
 * 두 문자열은 반드시 같아야 한다.
 */
export const HEALTH_PROMPT_MARKER = '[이 현장에만 추가로 볼 것]';

/**
 * 프롬프트 칸이 비어 있을 때 채워 두는 기본 문구. "지금 무엇을 점검하는지" 를
 * 보여 주어 이미 하는 것을 또 지시하지 않게 한다. 점검 항목이 바뀌면
 * docs/SITE_HEALTH.md 와 함께 이 문구도 고친다.
 */
export const DEFAULT_HEALTH_PROMPT = [
  '[기본 점검 항목 — 매일 아침 자동으로 확인합니다]',
  '· 컨테이너: 통합서버·영상처리서버의 기대 컨테이너 실행 여부, 재시작 횟수, 헬스 상태',
  '· 자원: CPU 사용률, 메모리 여유, 부하(코어 수 대비), 디스크 사용률과 남은 용량',
  '· 로그: 최근 24시간 error/panic/fatal 건수와 마지막 줄',
  '· 핑: IP/네트워크 스프레드시트에 등록된 현장 기기 IP 전체 (무응답 대수와 목록. 등급에는 반영하지 않습니다)',
  '',
  HEALTH_PROMPT_MARKER,
  '',
].join('\n');

export interface HealthTargetCardProps {
  projectId: number;
}

interface RowState {
  id: number | null;
  host: string;
  user: string;
  port: string;
  note: string;
  /** 통합서버 행에만 쓴다 — 떠 있어야 하는 컨테이너 이름 패턴(쉼표 구분). */
  services: string;
  password: string;
  /** 영상처리서버 표시 이름 — Teams 본문에 IP 대신 이 이름이 나간다. */
  name: string;
}

/** 방재실 하나 — 방재실이 하나인 현장은 이름이 빈 방재실 하나다. */
interface Room {
  key: string;
  name: string;
  /** 설정 파일 폴더(docker/deploy/sites/<값>/) — 통합서버 행에 저장. 비면 현장 코드. */
  configSite: string;
  server: RowState;
  pc: RowState;
  nodes: RowState[];
  nvrs: RowState[];
}

const EMPTY: RowState = { id: null, host: '', user: '', port: '', note: '', services: '', password: '', name: '' };

const toRow = (d?: SiteNetworkDevice): RowState =>
  d
    ? {
        id: d.id,
        host: d.ip_address || '',
        user: d.username || '',
        port: d.port ? String(d.port) : '',
        note: d.note || '',
        services: d.expected_services || '',
        password: d.ssh_password || '',
        name: d.name && d.name !== CATEGORY_VIDEO_SERVER && d.name !== CATEGORY_NVR ? d.name : '',
      }
    : { ...EMPTY };

/** IPv4 또는 호스트명. 잡이 ssh 인자로 그대로 쓰므로 공백·따옴표 같은 건 막는다. */
const HOST_RE = /^[A-Za-z0-9.\-_]+$/;
/** 컨테이너 이름 패턴 하나. 백엔드(site-health) 의 토큰 규칙과 같다. */
const SERVICE_RE = /^[A-Za-z0-9._@:-]{1,64}$/;

const splitServices = (v: string) =>
  v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

let roomSeq = 0;
const newRoom = (name = ''): Room => ({
  key: `room-${++roomSeq}`,
  name,
  configSite: '',
  server: { ...EMPTY },
  pc: { ...EMPTY },
  nodes: [],
  nvrs: [],
});

/**
 * 장비 목록을 방재실로 묶는다. 방재실 순서는 처음 등록한 장비 순(id 오름차순).
 * 같은 방재실에 같은 category 가 여러 개면 먼저 등록한 것을 쓴다 — 백엔드가
 * 인벤토리를 만들 때와 같은 규칙이어야 화면과 실제가 어긋나지 않는다.
 */
function toRooms(list: SiteNetworkDevice[]): Room[] {
  const sorted = [...list]
    .filter((d) =>
      [CATEGORY_INTEGRATION_SERVER, CATEGORY_ACCESS_PC, CATEGORY_VIDEO_SERVER, CATEGORY_NVR].includes(d.category),
    )
    .sort((a, b) => a.id - b.id);
  const rooms: Room[] = [];
  const byName = new Map<string, Room>();
  for (const d of sorted) {
    const name = (d.health_group || '').trim();
    let r = byName.get(name);
    if (!r) {
      r = newRoom(name);
      byName.set(name, r);
      rooms.push(r);
    }
    if (d.category === CATEGORY_INTEGRATION_SERVER && !r.server.id) {
      r.server = toRow(d);
      r.configSite = d.config_site || '';
    }
    else if (d.category === CATEGORY_ACCESS_PC && !r.pc.id) r.pc = toRow(d);
    else if (d.category === CATEGORY_VIDEO_SERVER) r.nodes.push(toRow(d));
    else if (d.category === CATEGORY_NVR) r.nvrs.push(toRow(d));
  }
  return rooms.length ? rooms : [newRoom()];
}

function validateRoom(room: Room, label: string): string | null {
  if (!room.server.host.trim()) return `${label}통합서버 주소는 필수입니다.`;
  const rows: ReadonlyArray<readonly [string, RowState]> = [
    ['통합서버', room.server],
    ['접속 PC', room.pc],
    ...room.nodes.map((n, i) => [n.name.trim() || `영상처리서버 ${i + 1}`, n] as const),
    ...room.nvrs.map((n, i) => [n.name.trim() || `NVR ${i + 1}`, n] as const),
  ];
  for (const [name, r] of rows) {
    if (!r.host.trim()) continue;
    if (!HOST_RE.test(r.host.trim())) {
      return `${label}${name} 주소에 쓸 수 없는 문자가 있습니다 (IP 또는 호스트명).`;
    }
    if (r.port.trim()) {
      const p = Number(r.port);
      if (!Number.isInteger(p) || p < 1 || p > 65535) {
        return `${label}${name} 포트는 1~65535 사이여야 합니다.`;
      }
    }
  }
  for (const svc of splitServices(room.server.services)) {
    if (!SERVICE_RE.test(svc)) {
      return `${label}기대 컨테이너 '${svc}' 에 쓸 수 없는 문자가 있습니다.`;
    }
  }
  return null;
}

/** 설정 코드 = 폴더 이름. 백엔드 entity.ValidConfigSite 와 같은 규칙. */
const CONFIG_SITE_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

function validate(rooms: Room[]): string | null {
  if (rooms.length > 1) {
    const names = rooms.map((r) => r.name.trim());
    if (names.some((n) => !n)) return '방재실이 여러 곳이면 방재실마다 이름을 적어 주세요 (예: 2블럭 방재실).';
    if (new Set(names).size !== names.length) return '방재실 이름이 겹칩니다.';
    // 방재실마다 설정 폴더가 따로다 — 현장 코드 하나로는 어느 방재실 것인지 정할 수 없다.
    const codes = rooms.map((r) => r.configSite.trim());
    if (codes.some((c) => !c)) return '방재실이 여러 곳이면 방재실마다 설정 코드를 적어 주세요 (예: jg2bl).';
    if (new Set(codes).size !== codes.length) return '방재실 설정 코드가 겹칩니다.';
  }
  for (const r of rooms) {
    const code = r.configSite.trim();
    if (code && !CONFIG_SITE_RE.test(code)) {
      return `설정 코드 '${code}' 는 소문자·숫자·하이픈만 쓸 수 있습니다 (예: jg2bl).`;
    }
  }
  for (const r of rooms) {
    const msg = validateRoom(r, rooms.length > 1 ? `[${r.name.trim()}] ` : '');
    if (msg) return msg;
  }
  return null;
}

const HealthTargetCard: React.FC<HealthTargetCardProps> = ({ projectId }) => {
  // 네트워크 스프레드시트와 같은 기준 — 백엔드 POST/PUT/DELETE 권한과 맞춘다.
  const role = getUserInfo()?.role;
  const writable = role === 'admin' || role === 'editor';

  const [rooms, setRooms] = useState<Room[]>([newRoom()]);
  // 현장 설정의 추가 검토 지시 — 매일 요약 때 Claude 에게 그대로 넘어간다.
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // 화면에서 지운 장비(영상처리서버·NVR 행, 방재실 통째) — 저장 때 서버에서도 지운다.
  const [removedIds, setRemovedIds] = useState<number[]>([]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      SiteNetworkDeviceService.list(projectId),
      SiteProjectService.getProject(projectId).catch(() => null),
    ])
      .then(([list, project]) => {
        if (cancelled) return;
        setPrompt(project?.health_prompt || DEFAULT_HEALTH_PROMPT);
        setRooms(toRooms(list));
        setRemovedIds([]);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '접속 정보 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => load(), [load]);

  const patchRoom = (key: string, patch: Partial<Room>) =>
    setRooms((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // 한 줄을 현재 상태에 맞춰 생성/수정/삭제한다. 주소를 비우면 등록을 지운다 —
  // 접속 PC 를 없앴는데 옛 값이 남아 있으면 잡이 죽은 PC 를 계속 경유한다.
  const persist = async (cat: string, row: RowState, name: string, group: string, configSite = '') => {
    const host = row.host.trim();
    if (!host) {
      if (row.id) await SiteNetworkDeviceService.delete(row.id);
      return;
    }
    const isNvr = cat === CATEGORY_NVR;
    const body = {
      category: cat,
      name: ((cat === CATEGORY_VIDEO_SERVER || isNvr) && row.name.trim()) || name,
      ip_address: host,
      // NVR 은 핑만 하므로 계정·포트가 없다.
      username: isNvr ? undefined : row.user.trim() || undefined,
      port: isNvr ? null : row.port.trim() ? Number(row.port) : null,
      note: row.note.trim() || undefined,
      // 빈 문자열도 보내야 지운 비밀번호가 서버에서도 지워진다.
      ...(isNvr ? {} : { ssh_password: row.password }),
      // 통합서버 행에만 담는다. 빈 문자열도 보내야 지운 값이 서버에서도 지워진다.
      ...(cat === CATEGORY_INTEGRATION_SERVER
        ? { expected_services: splitServices(row.services).join(','), config_site: configSite.trim() }
        : {}),
      health_group: group,
    };
    if (row.id) await SiteNetworkDeviceService.update(row.id, body);
    else await SiteNetworkDeviceService.create(projectId, body);
  };

  const save = async () => {
    const msg = validate(rooms);
    if (msg) {
      setError(msg);
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // 방재실이 하나면 이름이 있어도 그대로 저장한다(비워 두면 예전과 같다).
      for (const r of rooms) {
        const group = r.name.trim();
        await persist(CATEGORY_INTEGRATION_SERVER, r.server, '통합서버', group, r.configSite);
        await persist(CATEGORY_ACCESS_PC, r.pc, '접속 PC', group);
        for (const [i, n] of r.nodes.entries()) {
          await persist(CATEGORY_VIDEO_SERVER, n, `영상처리서버 ${i + 1}`, group);
        }
        for (const [i, n] of r.nvrs.entries()) {
          await persist(CATEGORY_NVR, n, `NVR ${i + 1}`, group);
        }
      }
      for (const id of removedIds) {
        await SiteNetworkDeviceService.delete(id);
      }
      setRemovedIds([]);
      await SiteProjectService.updateProject(projectId, { health_prompt: prompt.trim() });
      setSaved(true);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const removeRoom = (room: Room) => {
    const ids = [room.server, room.pc, ...room.nodes, ...room.nvrs]
      .map((r) => r.id)
      .filter((v): v is number => v != null);
    setRemovedIds((prev) => [...prev, ...ids]);
    setRooms((prev) => prev.filter((r) => r.key !== room.key));
  };

  const row = (
    label: string,
    hint: string,
    value: RowState,
    set: (v: RowState) => void,
  ) => (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
      <TextField
        label={`${label} 주소`}
        value={value.host}
        onChange={(e) => set({ ...value, host: e.target.value })}
        size="small"
        disabled={!writable || saving}
        helperText={hint}
        sx={{ flex: 2, minWidth: 0 }}
      />
      <TextField
        label="SSH 계정"
        value={value.user}
        onChange={(e) => set({ ...value, user: e.target.value })}
        size="small"
        disabled={!writable || saving}
        helperText="비우면 점검 잡의 기본 계정"
        sx={{ flex: 1, minWidth: 0 }}
      />
      <TextField
        label="SSH 비밀번호"
        type="password"
        value={value.password}
        onChange={(e) => set({ ...value, password: e.target.value })}
        size="small"
        disabled={!writable || saving}
        helperText="키 인증이 안 되면 이걸로 붙는다"
        autoComplete="new-password"
        sx={{ flex: 1, minWidth: 0 }}
      />
      <TextField
        label="포트"
        value={value.port}
        onChange={(e) => set({ ...value, port: e.target.value.replace(/[^0-9]/g, '') })}
        size="small"
        disabled={!writable || saving}
        helperText="비우면 22"
        sx={{ width: { xs: '100%', md: 110 }, flexShrink: 0 }}
      />
      <TextField
        label="메모"
        value={value.note}
        onChange={(e) => set({ ...value, note: e.target.value })}
        size="small"
        disabled={!writable || saving}
        sx={{ flex: 1.5, minWidth: 0 }}
      />
    </Stack>
  );

  // 여러 대인 목록(영상처리서버·NVR) 머리 — 제목·설명·[추가].
  const listHead = (title: string, hint: string, onAdd: () => void, testId: string) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
      {writable && (
        <Button size="small" startIcon={<AddIcon />} disabled={saving} onClick={onAdd} data-testid={testId}>
          추가
        </Button>
      )}
    </Stack>
  );

  const renderRoom = (room: Room, idx: number) => {
    const multi = rooms.length > 1;
    const set = (patch: Partial<Room>) => patchRoom(room.key, patch);
    return (
      <Stack key={room.key} spacing={2} data-testid={`health-room-${idx}`}>
        {multi && (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="방재실 이름"
              value={room.name}
              placeholder="예: 2블럭 방재실"
              onChange={(e) => set({ name: e.target.value })}
              size="small"
              required
              disabled={!writable || saving}
              sx={{ width: 260 }}
              inputProps={{ 'data-testid': `health-room-name-${idx}` }}
            />
            {writable && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={saving}
                onClick={() => removeRoom(room)}
              >
                방재실 삭제
              </Button>
            )}
          </Stack>
        )}
        {row('통합서버 (필수)', '명령이 실제로 실행되는 서버', room.server, (v) => set({ server: v }))}
        <TextField
          label="기대 컨테이너"
          value={room.server.services}
          onChange={(e) => set({ server: { ...room.server, services: e.target.value } })}
          size="small"
          disabled={!writable || saving}
          helperText="통합서버에 떠 있어야 하는 컨테이너 이름(부분 문자열), 쉼표 구분. 하나라도 죽으면 그 현장은 🔴. 비우면 기본값(mysql, redis, smartparking-router)"
          placeholder="mysql, redis, smartparking-router, lights"
          fullWidth
        />
        <TextField
          label="설정 코드"
          value={room.configSite}
          onChange={(e) => set({ configSite: e.target.value })}
          size="small"
          required={multi}
          disabled={!writable || saving}
          helperText={
            multi
              ? '이 방재실 설정 파일 폴더 docker/deploy/sites/<코드>/ (예: jg2bl). 배포 화면의 현장 설정 파일이 이 폴더를 엽니다.'
              : '설정 파일 폴더 docker/deploy/sites/<코드>/. 비우면 현장 코드를 씁니다.'
          }
          placeholder={multi ? 'jg2bl' : '비우면 현장 코드'}
          sx={{ maxWidth: 360 }}
          inputProps={{ 'data-testid': `health-room-config-site-${idx}`, style: { fontSize: 16 } }}
        />
        {row('접속 PC (선택)', '경유지. 없으면 비워 두세요', room.pc, (v) => set({ pc: v }))}

        <Box>
          {listHead(
            `영상처리서버 (${room.nodes.length}대)`,
            '통합서버를 경유해 CPU·메모리·디스크·컨테이너를 확인합니다. 대수 제한 없음.',
            () => set({ nodes: [...room.nodes, { ...EMPTY }] }),
            `health-add-node-${idx}`,
          )}
          <Stack spacing={1}>
            {room.nodes.map((n, i) => (
              <Stack key={n.id ?? `new-${i}`} direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="이름"
                  value={n.name}
                  placeholder={`영상처리서버 ${i + 1}`}
                  onChange={(e) =>
                    set({ nodes: room.nodes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })
                  }
                  size="small"
                  disabled={!writable || saving}
                  sx={{ width: 150, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {row('영상처리서버', '현장 LAN 주소', n, (v) =>
                    set({ nodes: room.nodes.map((x, j) => (j === i ? v : x)) }),
                  )}
                </Box>
                {writable && (
                  <IconButton
                    aria-label={`영상처리서버 ${i + 1} 삭제`}
                    size="small"
                    disabled={saving}
                    onClick={() => {
                      if (n.id) setRemovedIds((prev) => [...prev, n.id as number]);
                      set({ nodes: room.nodes.filter((_, j) => j !== i) });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box>
          {listHead(
            `NVR (${room.nvrs.length}대)`,
            '통합서버에서 핑으로 응답 여부만 확인합니다. 이름과 IP 만 적으면 됩니다.',
            () => set({ nvrs: [...room.nvrs, { ...EMPTY }] }),
            `health-add-nvr-${idx}`,
          )}
          <Stack spacing={1}>
            {room.nvrs.map((n, i) => (
              <Stack key={n.id ?? `new-nvr-${i}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <TextField
                  label="이름"
                  value={n.name}
                  placeholder={`NVR ${i + 1}`}
                  onChange={(e) =>
                    set({ nvrs: room.nvrs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })
                  }
                  size="small"
                  disabled={!writable || saving}
                  sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
                />
                <TextField
                  label="NVR IP"
                  value={n.host}
                  onChange={(e) =>
                    set({ nvrs: room.nvrs.map((x, j) => (j === i ? { ...x, host: e.target.value } : x)) })
                  }
                  size="small"
                  disabled={!writable || saving}
                  sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                  inputProps={{ 'data-testid': `health-nvr-ip-${idx}-${i}` }}
                />
                <TextField
                  label="메모"
                  value={n.note}
                  onChange={(e) =>
                    set({ nvrs: room.nvrs.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)) })
                  }
                  size="small"
                  disabled={!writable || saving}
                  sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}
                />
                {writable && (
                  <IconButton
                    aria-label={`NVR ${i + 1} 삭제`}
                    size="small"
                    disabled={saving}
                    onClick={() => {
                      if (n.id) setRemovedIds((prev) => [...prev, n.id as number]);
                      set({ nvrs: room.nvrs.filter((_, j) => j !== i) });
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="health-target-card">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <HealthIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          원격 상태 점검 접속 정보
        </Typography>
        <Typography variant="caption" color="text.secondary">
          관리 서버가 이 주소로 붙어 상태를 확인합니다 (현장 목록 서버 상태 10분마다 · Teams 보고 매일 아침)
        </Typography>
      </Stack>

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        아래 네트워크 표와 목적이 다릅니다 — 표는 사람이 보는 IP 대장이고, 여기는
        점검이 기계로 읽는 값입니다. 관리 서버의 SSH 키가 현장에 등록되어 있으면 키로, 아니면
        여기 적은 비밀번호로 붙습니다. 접속 PC 가 곧 통합서버인 현장은 접속 PC 를 비워 두면 직접 접속합니다.
        방재실이 여러 곳이면 [방재실 추가]로 방재실마다 따로 등록하세요.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {rooms.map((room, idx) => (
            <React.Fragment key={room.key}>
              {idx > 0 && <Divider />}
              {renderRoom(room, idx)}
            </React.Fragment>
          ))}
          {writable && (
            <Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={saving}
                onClick={() => setRooms((prev) => [...prev, newRoom()])}
                data-testid="health-add-room"
              >
                방재실 추가
              </Button>
            </Box>
          )}

          <TextField
            label="추가 검토 지시 (프롬프트)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            multiline
            minRows={8}
            size="small"
            disabled={!writable || saving}
            fullWidth
            helperText={`위쪽 기본 점검 항목은 이미 매일 확인하는 것이니 그대로 두고, "${HEALTH_PROMPT_MARKER}" 아래에만 이 현장에서 더 볼 것을 적으세요. 예) 주차유도 워커 오류 로그가 늘면 원인 후보를 적어 줘. 수집한 값(컨테이너·자원·로그·핑) 범위 안에서만 답합니다.`}
          />
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saved && !error && (
        <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSaved(false)}>
          저장했습니다 — 다음 점검부터 반영됩니다.
        </Alert>
      )}

      {writable && !loading && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button variant="contained" size="small" onClick={save} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default HealthTargetCard;
